/* eslint-disable no-underscore-dangle */
import _ from 'underscore';
import ko from 'knockout';
import doubleListLayoutTemplate from '../../template/double-list-box-layout.pug';
import sourceItemTemplate from '../../template/double-list-box-source-item.pug';
import selectedItemTemplate from '../../template/double-list-box-selected-item.pug';
import selectedItemLayoutTemplate from '../../template/double-list-box-selected.pug';
import sourceItemLayoutTemplate from '../../template/double-list-box-source-layout.pug';

const sourceItems = viewModel => (request, cb) => {
  const selectedDimension = viewModel.selectedDimension();

  if (_.isArray(_.result(selectedDimension, 'AvailableItems'))) {
    return cb(null, {
      items: selectedDimension.AvailableItems,
      total: selectedDimension.AvailableItems.length,
    });
  }
  return cb(null, {
    items: [],
    total: 0,
  });
};

const itemIdCallback = () => item => (item && item.Id ? item.Id.toLowerCase() : null);

const sortList = (list, key, descending) => {
  list.sort((item1, item2) => {
    if (key === 'Name') {
      return descending ? item2[key].toLowerCase().localeCompare(item1[key].toLowerCase()) :
        item1[key].toLowerCase().localeCompare(item2[key].toLowerCase());
    }
    const item1Num = Number.isNaN(+item1[key]) ? 0 : +item1[key];
    const item2Num = Number.isNaN(+item2[key]) ? 0 : +item2[key];
    return descending ? item2Num - item1Num : item1Num - item2Num;
  });
};

const sortLeftColumn = (view, key) => {
  const { doubleListBox } = view;
  // remove other columns' sort status
  _.each(doubleListBox._viewModel.customData.leftSorted, sortedColumn => sortedColumn(false));

  const descending = !doubleListBox._viewModel.customData.leftDescending[key]();
  const list = view.viewModel.selectedDimension().AvailableItems;

  // use in-place sort
  sortList(list, key, descending);
  doubleListBox._viewModel.customData.leftSorted[key](true);
  doubleListBox._viewModel.customData.leftDescending[key](descending);
  doubleListBox.refreshSource();
};

const sortRightColumn = (view, key) => {
  const { doubleListBox } = view;
  // remove other columns' sort status
  _.each(doubleListBox._viewModel.customData.rightSorted, sortedColumn => sortedColumn(false));

  const descending = !doubleListBox._viewModel.customData.rightDescending[key]();
  const list = doubleListBox._viewModel.selectedItems();

  // use in-place sort
  sortList(list, key, descending);
  doubleListBox._viewModel.customData.rightSorted[key](true);
  doubleListBox._viewModel.customData.rightDescending[key](descending);
  // notify that observable array contents have changed.
  doubleListBox.setSelection([]);
  doubleListBox.setSelection(list);
};

export const resetSort = (view) => {
  const { doubleListBox } = view;
  const doubleListCustomData = _.result(doubleListBox._viewModel, 'customData', {}); // eslint-disable-line
  const leftSorted = _.result(doubleListCustomData, 'leftSorted', {});
  const rightSorted = _.result(doubleListCustomData, 'rightSorted', {});
  const leftDescending = _.result(doubleListCustomData, 'leftDescending', {});
  const rightDescending = _.result(doubleListCustomData, 'rightDescending', {});
  // since ko vm is not emunerable, could not use Object.keys
  const leftKeys = ['Name', 'Count', 'Clicks', 'Impressions', 'Conversions'];
  const rightKeys = ['Name', 'Count'];
  leftKeys.forEach((key) => {
    leftSorted[key](false);
    leftDescending[key](false);
  });
  rightKeys.forEach((key) => {
    rightSorted[key](false);
    rightDescending[key](false);
  });
};

export function getDoubleListBoxConfig({ deps, view }) {
  return {
    el: view.$('#double-list-box'),
    data: {
      sourceItems: sourceItems(view.viewModel),
      itemIdField: 'Id',
      itemIdCallback: itemIdCallback(),
      useCallbackForFetchOnly: true,
      pageSize: 100,
    },
    itemActions: {},
    features: {
      paging: true,
      filtering: false,
      dragAndDrop: false,
    },
    templates: {
      layoutTemplate: doubleListLayoutTemplate({ i18n: deps.i18nGlobal }),
      sourceItemLayoutTemplate: sourceItemLayoutTemplate({ i18n: deps.i18nGlobal }),
      sourceItemContentTemplate: sourceItemTemplate(),
      selectedItemContentTemplate: selectedItemTemplate(),
      selectedItemLayoutTemplate: selectedItemLayoutTemplate({ i18n: deps.i18nGlobal }),
    },
    customData: {
      textAreaValue: ko.observable(),
      isBulkShown: ko.computed(() => view.viewModel.showBulkPanel()),
      leftDescending: {
        Name: ko.observable(false),
        Count: ko.observable(false),
        Clicks: ko.observable(false),
        Impressions: ko.observable(false),
        Conversions: ko.observable(false),
      },
      leftSorted: {
        Name: ko.observable(false),
        Count: ko.observable(false),
        Clicks: ko.observable(false),
        Impressions: ko.observable(false),
        Conversions: ko.observable(false),
      },
      rightDescending: {
        Name: ko.observable(false),
        Count: ko.observable(false),
      },
      rightSorted: {
        Name: ko.observable(false),
        Count: ko.observable(false),
      },
      sortLeftNameColumn: () => sortLeftColumn(view, 'Name'),
      sortLeftCountColumn: () => sortLeftColumn(view, 'Count'),
      sortLeftClicksColumn: () => sortLeftColumn(view, 'Clicks'),
      sortLeftImpressionsColumn: () => sortLeftColumn(view, 'Impressions'),
      sortLeftConversionsColumn: () => sortLeftColumn(view, 'Conversions'),
      sortRightNameColumn: () => sortRightColumn(view, 'Name'),
      sortRightCountColumn: () => sortRightColumn(view, 'Count'),
      isBulkAddEnabled: ko.computed(() => (view.viewModel.selectedDimension().IsBulkEnabled)),
      rightColumnHeader: ko.computed(() => (view.viewModel.selectedDimension().DisplayName)),
      countHeader: deps.i18nGlobal.getString('ProductGroup_ProductsCount'),
      clicksHeader: deps.i18n.getString('ProductGroupsGrid_Column_Clicks'),
      impressionsHeader: deps.i18n.getString('ProductGroupsGrid_Column_Impressions'),
      conversionsHeader: deps.i18n.getString('ProductGroupsGrid_Column_Conversions'),
      leftColumnHeader: ko.computed(() => (view.viewModel.selectedDimension().DisplayName)),
    },
  };
}
